import React from 'react';
import { Grid, Stack, Card, Tooltip, Box, Typography } from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_Cell,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_RowSelectionState,
  type MRT_TableInstance,
  type MRT_VisibilityState,
  MRT_GlobalFilterTextField,
} from 'material-react-table';
import { Delete, Edit as EditIcon, AutoAwesome, List, Add, Search } from '@mui/icons-material';
import { IconOverTextButton } from '../common/IconOverTextButton';
import { Division, LineItemForPrediction } from '../glossary/glossary.type';
import { usePrompt } from '../AppPrompt';
import { compareArrays } from '../report/report.util';
import { toast } from 'react-toastify';
import LineItemsBulkdEditDialog from '../report/LineItemBulkEditDialog';
import { ReportGeneralInfo, ReportLineItemBulkEditFormValues } from '../report/report.type';
import CreateLineItemDialog from '../report/CreateLineItemDialog';
import { REPORT_COLORS, TOTAL_ID } from '../report/constants';
import { ProjectAnalysis } from './project-analysis.type';
import WhiteFab from '../common/WhiteFab';
import { ProjectDraw, ProjectLineItemDTO } from '../project/project.type';
import { CreateDrawLineItemDTO } from '../project/project-draws/project-draws.type';
import { formatCurrency, formatNumber, formatPercentage } from '../../common/utils/common.util';
import { NEGATIVE_NUMBER_COLOR } from '../../common/constants/general';
import { calculateUnitCost } from '../report/report.util';
import { ALLOWANCES_TOTAL_ID } from '../report/constants';
import { LineItemUnitEnum } from '../glossary/glossary.type';
import { useMRTStyling } from '../../hooks/useMRTStyling';

const unitsList = [...Object.values(LineItemUnitEnum).map(unit => ({ value: unit, label: unit })), { label: 'None', value: '' }];

interface Props {
  projectAnalysis: ProjectAnalysis;
  division: Division | null;
  gridData: ProjectLineItemDTO[];
  divisions: Division[];
  updateProjectAnalysis: (updatedProjectAnalysis: ProjectAnalysis) => void;
  updateLineItem: (updatedLineItem: ProjectLineItemDTO) => void;
  setSelectedLineItem: (lineItem: ProjectLineItemDTO | null) => void;
  setLineItemEditDialogOpen: (isOpen: boolean) => void;
  handleOpenProjectDialog: (division: Division) => void;
  refetchProjectAnalysis: () => void;
  hardCostSubTotal?: number;
  reportGeneralInfo: ReportGeneralInfo;
  openAutoAssignDialog?: (lineItemsToAutoAssign: LineItemForPrediction[]) => void;
  predictingDivisions?: boolean;
  projectDraws?: ProjectDraw[];
}

interface CustomToolbarProps {
  headingText: string;
  handleOpenProjectDialog: (division: Division) => void;
  division: Division | null;
  handleOpenLineItemDialog: () => void;
  handleAutoAssignDivisions: () => void;
  predictingDivisions?: boolean;
  isSearchOpen: boolean;
  setIsSearchOpen: (open: boolean) => void;
  table: MRT_TableInstance<any>;
}

const renderColCell = (highlightMismatch = false) => {
  // eslint-disable-next-line react/prop-types
  const CellRenderer = ({ cell, row }: { cell: MRT_Cell<ProjectLineItemDTO>; row: MRT_Row<ProjectLineItemDTO> }) => {
    const value = cell.getValue<number>();
    const isTotalRow = row.id === TOTAL_ID;

    let textColor = 'inherit';

    if (value < 0 && !isTotalRow) {
      textColor = NEGATIVE_NUMBER_COLOR;
    }

    return (
      <div
        style={{
          color: textColor,
          fontWeight: '300',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          overflow: 'hidden',
          justifyContent: 'flex-end',
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: '1',
            minWidth: 0,
          }}
        >
          {formatCurrency(value?.toString(), true)}
        </span>
      </div>
    );
  };

  CellRenderer.displayName = 'NumberCell';
  return CellRenderer;
};

// Description cell renderer
// eslint-disable-next-line react/prop-types
const DescriptionCellRenderer = ({ cell, row }: { cell: MRT_Cell<ProjectLineItemDTO>; row: MRT_Row<ProjectLineItemDTO> }) => {
  const description = cell.getValue<string>();
  const isTotalRow = row.id === TOTAL_ID;

  if (isTotalRow) {
    return <span style={{ fontWeight: 'bold' }}>{description}</span>;
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: '1',
          minWidth: 0,
        }}
      >
        {description}
      </span>
    </div>
  );
};

export const ProjectAnalysisDivisionTable: React.FC<Props> = ({
  projectAnalysis,
  updateProjectAnalysis,
  updateLineItem,
  division,
  gridData,
  divisions,
  setLineItemEditDialogOpen,
  setSelectedLineItem,
  handleOpenProjectDialog,
  refetchProjectAnalysis,
  hardCostSubTotal,
  reportGeneralInfo,
  openAutoAssignDialog,
  predictingDivisions = false,
  projectDraws,
}) => {
  const { getConfirmation } = usePrompt();
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = React.useState(false);
  const [isLineItemDialogOpen, setIsLineItemDialogOpen] = React.useState(false);
  const [selectedLineItems, setSelectedLineItems] = React.useState<ProjectLineItemDTO[]>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<MRT_VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>({});
  const [sorting, setSorting] = React.useState<any>([]);
  const [isFullScreen, setIsFullScreen] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const { getOnSortingChangeWithToast } = useMRTStyling<ProjectLineItemDTO>();

  const handleAutoAssignDivisions = () => {
    openAutoAssignDialog
      ? openAutoAssignDialog(
          gridData.map(lineItem => {
            return {
              id: lineItem.id,
              description: lineItem.lineItem.description,
              revisedBudget: lineItem?.originalBudget + lineItem?.changeOrder + lineItem?.reallocation,
            };
          }),
        )
      : toast.error('Auto-Assign feature not available');
  };

  React.useEffect(() => {
    const ids = Object.keys(rowSelection);
    const selectedLineItems = gridData.filter(li => ids.includes(li.id));
    setSelectedLineItems(selectedLineItems);
  }, [rowSelection, gridData]);

  const processRowUpdate = (updatedRow: any, originalRow: any) => {
    const same = compareArrays(updatedRow, originalRow);
    if (!same) {
      // Update the line item in the projectAnalysis state
      const updatedLineItems = projectAnalysis.lineItems.map(lineItem => {
        if (lineItem.id === updatedRow.id) {
          return { ...updatedRow, isEdited: true };
        }
        return lineItem;
      });
      updateProjectAnalysis({ ...projectAnalysis, lineItems: updatedLineItems });
    }
    return { ...updatedRow };
  };

  const handleOpenLineItemDialog = () => {
    setIsLineItemDialogOpen(true);
  };

  const handleCloseLineItemDialog = () => {
    setIsLineItemDialogOpen(false);
  };

  const saveLineItem = async (lineItemData: CreateDrawLineItemDTO) => {
    try {
      // Generate a unique temporary ID for new line items
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Store the new line item locally and mark it as edited
      const newLineItem: ProjectLineItemDTO = {
        ...lineItemData,
        id: lineItemData.id || tempId,
        isEdited: false,
        isCreated: true,
        revisedBudget: (lineItemData.originalBudget || 0) + (lineItemData.changeOrder || 0) + (lineItemData.reallocation || 0),
        cdccApproved: 0,
        reallocation: 0,
        changeOrder: 0,
        completedStoredToDate: 0,
        storedThisPeriod: lineItemData.storedThisPeriod ?? 0,
        completeThisPeriod: lineItemData.completeThisPeriod ?? 0,
        previousApplication: lineItemData.previousApplication ?? 0,
        quantity: lineItemData.quantity ?? 0,
        percentageComplete: 0,
        balanceToComplete: 0,
        retainage: lineItemData.retainage ?? 0,
        isIncluded: false,
        lineItem: {
          id: '',
          description: lineItemData.description,
          unit: lineItemData.unit ?? '',
          division: {
            id: lineItemData.divisionId ?? '',
            name: '',
            code: '',
          },
        },
        projectDrawId: lineItemData.projectDrawId,
        projectDrawName: lineItemData.projectDrawName ?? '',
        divisionId: lineItemData.divisionId,
      };

      // Update the projectAnalysis state globally
      updateProjectAnalysis({
        ...projectAnalysis,
        lineItems: [...projectAnalysis.lineItems, newLineItem],
      });
    } catch (error) {
      toast.error('Error saving line item');
      console.error(error);
    }
  };

  const handleBulkDelete = React.useCallback(async () => {
    const selectedIds = selectedLineItems.map(li => li.id);
    if (selectedIds.length === 0) {
      toast.error('Please select at least one row!');
      return;
    }
    const updatedLineItems = projectAnalysis.lineItems.map(lineItem => {
      if (selectedIds.includes(lineItem.id)) {
        return { ...lineItem, isDeleted: true };
      }
      return lineItem;
    });
    updateProjectAnalysis({ ...projectAnalysis, lineItems: updatedLineItems });
  }, [selectedLineItems, projectAnalysis, updateProjectAnalysis]);

  const handleDeleteReportLineItem = React.useCallback(
    async (id: string) => {
      const confirmed = await getConfirmation({ description: 'Are you sure you want to delete this line item?' });
      if (!confirmed) return;
      const updatedLineItems = projectAnalysis.lineItems.map(lineItem => {
        if (lineItem.id === id) {
          return { ...lineItem, isDeleted: true };
        }
        return lineItem;
      });
      updateProjectAnalysis({ ...projectAnalysis, lineItems: updatedLineItems });
    },
    [getConfirmation, projectAnalysis],
  );

  const handleBulkEditClick = React.useCallback(() => {
    if (selectedLineItems.length === 0) {
      toast.error('Please select at least one row!');
      return;
    }
    setBulkEditDialogOpen(true);
  }, [selectedLineItems]);

  const handleBulkEditSubmit = React.useCallback(
    (values: ReportLineItemBulkEditFormValues) => {
      if (selectedLineItems.length === 0) {
        toast.error('Failed to update. No line items found!');
        return;
      }
      const updatedLineItems = projectAnalysis.lineItems.map(lineItem => {
        if (selectedLineItems.find(li => li.id === lineItem.id)) {
          const updatedDivision = values.changeDivision ? (values.division ?? undefined) : lineItem.lineItem.division;
          const updatedLabels = values.changeLabel ? (values.label ? [values.label] : []) : (lineItem.labels ?? []);
          return {
            ...lineItem,
            lineItem: {
              ...lineItem.lineItem,
              division: updatedDivision,
            },
            labels: updatedLabels,
            updateDivisionGlobally: values.updateDivisionGlobally,
            isEdited: true,
          };
        }
        return lineItem;
      });
      updateProjectAnalysis({ ...projectAnalysis, lineItems: updatedLineItems });
      setBulkEditDialogOpen(false);
    },
    [selectedLineItems, projectAnalysis, updateProjectAnalysis],
  );

  const handleSaveCell = (cell: MRT_Cell<ProjectLineItemDTO>, value: any) => {
    const updatedData = { ...cell.row.original };

    if (cell.column.id === 'division') {
      updatedData.lineItem = {
        ...updatedData.lineItem,
        division: divisions.find(d => d.id === value) || undefined,
      };
    } else {
      // Handle numeric fields
      const parsedValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
      (updatedData as any)[cell.column.id] = parsedValue;

      // Recalculate revised budget when originalBudget, reallocation, or changeOrder changes
      if (['originalBudget', 'reallocation', 'changeOrder'].includes(cell.column.id)) {
        updatedData.revisedBudget = (updatedData.originalBudget || 0) + (updatedData.reallocation || 0) + (updatedData.changeOrder || 0);
      }
    }

    processRowUpdate(updatedData, cell.row.original);
  };

  // Filter line items to exclude those marked as deleted
  const filteredLineItems = React.useMemo(() => {
    return gridData.filter(item => !item.isDeleted);
  }, [gridData]);

  const totalsLineItem: ProjectLineItemDTO = React.useMemo(() => {
    const isPercentageDivision = division?.code && ['1.00', '17.00', '18.00'].includes(division.code);
    const isAllowances = division?.code === 'allowances';

    // Calculate revised budget total
    const revisedBudgetTotal = filteredLineItems.reduce((acc, item) => {
      const revisedBudget = (item.originalBudget || 0) + (item.reallocation || 0) + (item.changeOrder || 0);
      return acc + revisedBudget;
    }, 0);

    // Calculate quantity based on division type, similar to getTotalsRow logic
    let calculatedQuantity = 0;
    if (hardCostSubTotal && isPercentageDivision) {
      calculatedQuantity = (revisedBudgetTotal / hardCostSubTotal) * 100;
    } else {
      // For regular divisions, use gross building SF if available, otherwise sum individual quantities
      const grossBuildingSF = reportGeneralInfo?.grossBuildingSF || 0;
      if (grossBuildingSF > 0) {
        calculatedQuantity = revisedBudgetTotal / grossBuildingSF;
      } else {
        calculatedQuantity = filteredLineItems.reduce((acc, item) => acc + (item.quantity || 0), 0);
      }
    }

    return {
      id: 'totals',
      lineItem: {
        id: 'totals',
        description: `${division?.name.toLocaleUpperCase() ?? 'NO DIVISION ASSIGNED (AND UNDEFINED CHANGE ORDERS)'} TOTALS`,
        division: undefined,
        unit: '',
      },
      originalBudget: filteredLineItems.reduce((acc, item) => acc + (item.originalBudget || 0), 0),
      reallocation: filteredLineItems.reduce((acc, item) => acc + (item.reallocation || 0), 0),
      changeOrder: filteredLineItems.reduce((acc, item) => acc + (item.changeOrder || 0), 0),
      previousApplication: filteredLineItems.reduce((acc, item) => acc + (item.previousApplication || 0), 0),
      completeThisPeriod: filteredLineItems.reduce((acc, item) => acc + (item.completeThisPeriod || 0), 0),
      storedThisPeriod: filteredLineItems.reduce((acc, item) => acc + (item.storedThisPeriod || 0), 0),
      retainage: filteredLineItems.reduce((acc, item) => acc + (item.retainage || 0), 0),
      revisedBudget: 0,
      cdccApproved: 0,
      completedStoredToDate: 0,
      percentageComplete: 0,
      balanceToComplete: 0,
      projectDrawId: '',
      projectDrawName: '',
      quantity: calculatedQuantity,
      isIncluded: true,
    };
  }, [filteredLineItems, division, hardCostSubTotal, reportGeneralInfo]);

  const columns = React.useMemo<MRT_ColumnDef<ProjectLineItemDTO>[]>(() => {
    const leftAlignedProps = {
      muiTableBodyCellProps: { align: 'left' as const },
      muiTableHeadCellProps: {
        align: 'left' as const,
        sx: (theme: any) => ({
          backgroundColor: division?.code && ['19.00', '20.00'].includes(division.code) ? REPORT_COLORS.GREEN : REPORT_COLORS.YELLOW,
          color: '#000000',
          fontWeight: '400',
          padding: '8px 16px',
          height: '60px',
          minHeight: '60px',
          border: 'none',
          position: 'relative',
          justifyContent: 'flex-start',
          lineHeight: '50px',
          verticalAlign: 'middle',
          '& .Mui-TableHeadCell-Content-Wrapper': {
            justifyContent: 'flex-start',
            alignItems: 'center',
            height: '100%',
            display: 'flex',
          },
          '&:not(:last-of-type)::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: '20%',
            height: '60%',
            width: '1px',
            backgroundColor: 'rgba(0, 0, 0, 0.15)',
            boxShadow: '0 0 4px rgba(0, 0, 0, 0.2)',
          },
        }),
      },
      muiTableFooterCellProps: {
        align: 'left' as const,
      },
    };

    const baseColumns: Omit<MRT_ColumnDef<ProjectLineItemDTO>, 'size'>[] = [
      {
        accessorKey: 'lineItem.description',
        header: 'Description',
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell, row }) => <DescriptionCellRenderer cell={cell} row={row} />,
        Footer: () => totalsLineItem.lineItem.description,
        enableEditing: false,
      },
      {
        accessorKey: 'originalBudget',
        header: 'Original Budget',
        enableEditing: true,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()?.toString() ?? '0', true),
        muiEditTextFieldProps: ({ cell }) => ({
          sx: {
            '& .MuiInputBase-input': { textAlign: 'left' },
          },
          onBlur: event => {
            handleSaveCell(cell, event.target.value);
          },
        }),
        ...leftAlignedProps,
        Footer: () => formatCurrency(totalsLineItem.originalBudget.toString(), true),
      },
      {
        accessorKey: 'revisedBudget',
        header: 'Revised Budget',
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => {
          const revisedBudget = (row.original.originalBudget || 0) + (row.original.reallocation || 0) + (row.original.changeOrder || 0);
          return formatCurrency(revisedBudget.toString(), true);
        },
        ...leftAlignedProps,
        enableEditing: false,
        Footer: () => {
          const revisedBudget = totalsLineItem.originalBudget + totalsLineItem.reallocation + totalsLineItem.changeOrder;
          return formatCurrency(revisedBudget.toString(), true);
        },
      },
      {
        accessorKey: 'quantity',
        header: 'Quantity',
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell, row }) => {
          const value = cell.getValue<number>();
          const isTotal = row.id === TOTAL_ID;
          const divisionCode = row.original.lineItem?.division?.code;
          const divisionName = row.original.lineItem?.description;
          const isPercentageDivision =
            (divisionCode && ['1.00', '17.00', '18.00'].includes(divisionCode)) ||
            (divisionName &&
              [
                'GENERAL CONDITIONS TOTALS',
                'GENERAL CONTRACTOR FEES / OVERHEAD & PROFIT TOTALS',
                'GENERAL CONTRACTOR CONTINGENCY TOTALS',
              ].includes(divisionName));

          if (isPercentageDivision && isTotal) return formatPercentage(value?.toString());
          if (isTotal) return formatCurrency(value?.toString(), true);
          if (row.original.id === ALLOWANCES_TOTAL_ID) return formatPercentage(value?.toString());

          return value && typeof value === 'number' && !isNaN(value) ? formatNumber(value, 2) : '';
        },
        muiEditTextFieldProps: ({ cell }) => ({
          sx: {
            '& .MuiInputBase-input': { textAlign: 'left' },
          },
          onBlur: event => {
            handleSaveCell(cell, event.target.value);
          },
        }),
        ...leftAlignedProps,
        Footer: () => {
          const isPercentageDivision = division?.code && ['1.00', '17.00', '18.00'].includes(division.code);
          if (isPercentageDivision) {
            return formatPercentage(totalsLineItem.quantity.toString());
          }
          return formatCurrency(totalsLineItem.quantity.toString(), true);
        },
      },
      {
        accessorKey: 'lineItem.unit',
        header: 'Unit',
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => cell.getValue<string>() || '',
        // eslint-disable-next-line react/prop-types
        Edit: ({ cell, table }) => {
          // eslint-disable-next-line react/prop-types
          const handleChange = (unit: string) => {
            // eslint-disable-next-line react/prop-types
            const updatedData = { ...cell.row.original };
            updatedData.lineItem = {
              ...updatedData.lineItem,
              unit: unit,
            };
            // eslint-disable-next-line react/prop-types
            processRowUpdate(updatedData, cell.row.original);
            // eslint-disable-next-line react/prop-types
            table.setEditingCell(null);
          };

          return (
            <select
              // eslint-disable-next-line react/prop-types
              value={cell.getValue<string>() || ''}
              onChange={e => handleChange(e.target.value)}
              style={{ width: '100%', padding: '8px' }}
            >
              {unitsList.map(unit => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          );
        },
        enableEditing: true,
        ...leftAlignedProps,
        Footer: () => {
          // Display appropriate unit based on division type
          const divisionCode = division?.code;
          const isPercentageDivision = ['1.00', '17.00', '18.00'].includes(divisionCode || '');
          return isPercentageDivision ? '%' : '/SF';
        },
      },
      {
        accessorKey: 'unitCost',
        header: '$ / Unit',
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => {
          const unitCost = calculateUnitCost(row.original);
          return unitCost && unitCost !== 0 ? formatCurrency(unitCost.toString()) : '--';
        },
        ...leftAlignedProps,
        enableEditing: false,
        Footer: () => '--',
      },
    ];

    const extendedColumns = baseColumns.map(col => {
      const columnId = col.accessorKey ?? col.id ?? '';

      // Use flexible sizing instead of fixed sizes
      const size = undefined; // Let Material React Table auto-size
      let minSize = 100; // Minimum width
      const maxSize = undefined; // No maximum width limit

      // Set specific minimum sizes for certain columns
      if (columnId === 'lineItem.description') {
        minSize = 250;
      } else if (columnId === 'lineItem.unit') {
        minSize = 80;
      } else if (columnId === 'quantity') {
        minSize = 120;
      } else if (columnId === 'unitCost') {
        minSize = 120;
      } else if (columnId === 'originalBudget') {
        minSize = 180;
      } else if (columnId === 'revisedBudget') {
        minSize = 180;
      } else {
        minSize = 120; // Default minimum for other columns
      }

      // Apply custom Cell to numeric fields
      let Cell = col.Cell;
      if (['originalBudget', 'revisedBudget'].includes(columnId)) {
        // eslint-disable-next-line react/prop-types
        Cell = ({ cell, row }) => renderColCell()({ cell, row });
      }

      return {
        ...col,
        size,
        minSize,
        maxSize,
        Cell,
      };
    });

    return extendedColumns;
  }, [division, totalsLineItem, divisions]);

  const table = useMaterialReactTable({
    columns,
    data: filteredLineItems,
    initialState: {
      showGlobalFilter: true,
      density: 'compact',
      columnPinning: { left: ['mrt-row-select', 'lineItem.description'], right: ['mrt-row-actions'] },
    },
    layoutMode: 'grid',
    enableColumnResizing: true,
    enableColumnPinning: true,
    enableGlobalFilter: true,
    enableColumnFilters: false,
    columnResizeMode: 'onChange',
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enablePagination: false,
    enableDensityToggle: false,
    enableColumnActions: false,
    enableStickyFooter: true,
    editDisplayMode: 'cell',
    enableEditing: true,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibilityModel,
    onSortingChange: getOnSortingChangeWithToast(sorting as any, setSorting as any),
    getRowId: row => row.id,
    positionActionsColumn: 'last',
    enableRowActions: true,
    renderRowActions: ({ row }) => (
      <Stack direction='row' spacing={1} alignItems='flex-end' justifyContent='center'>
        <Tooltip title='Edit Line Item' arrow={false}>
          <WhiteFab
            size='small'
            sx={{ width: 32, height: 32, minHeight: 32 }}
            icon={<EditIcon fontSize='small' />}
            textColor='primary'
            onClick={() => {
              setSelectedLineItem(row.original);
              setLineItemEditDialogOpen(true);
            }}
          />
        </Tooltip>
        <Tooltip title='Delete Line Item' arrow={false}>
          <WhiteFab
            size='small'
            sx={{ width: 32, height: 32, minHeight: 32 }}
            icon={<Delete fontSize='small' />}
            textColor='error'
            onClick={() => handleDeleteReportLineItem(row.original.id)}
          />
        </Tooltip>
      </Stack>
    ),
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 80,
        // eslint-disable-next-line react/prop-types
        Header: ({ table }) => (
          <Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
            <Tooltip title='Edit Selected Line Items' arrow={false}>
              <span>
                <WhiteFab
                  size='small'
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                  icon={<EditIcon fontSize='small' />}
                  textColor='primary'
                  onClick={handleBulkEditClick}
                  // eslint-disable-next-line react/prop-types
                  disabled={table.getSelectedRowModel().flatRows.length === 0}
                />
              </span>
            </Tooltip>
            <Tooltip title='Delete Selected Line Items' arrow={false}>
              <span>
                <WhiteFab
                  size='small'
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                  icon={<Delete fontSize='small' />}
                  textColor='error'
                  onClick={handleBulkDelete}
                  // eslint-disable-next-line react/prop-types
                  disabled={table.getSelectedRowModel().flatRows.length === 0}
                />
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    },
    muiTableContainerProps: {
      sx: {
        padding: '0px', // Inner padding inside scrollable area
        maxHeight: '800px', // Add max height to make table scrollable
        // Hide the gray empty row
        '& .MuiBox-root.css-kd8zx9': {
          display: 'none !important',
        },
        // Override MRT's pinned cell alignment to center
        '& .css-1jh1yab-MuiTableCell-root[data-pinned="true"]': {
          justifyContent: 'center !important',
        },
        // Ensure table body has enough height to push footer down
        // '& .MuiTableBody-root': {
        //   minHeight: '300px',
        //   display: 'unset',
        // },
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      let backgroundColor = '';

      if (row.original?.isEdited || row.original?.isCreated) {
        backgroundColor = REPORT_COLORS.UNSAVED_BACKGROUND_COLOR;
      }

      // Fallback to zebra striping if no custom background
      if (!backgroundColor) {
        backgroundColor = row.index % 2 === 0 ? '#ffffff' : '#f9f9f9';
      }

      return {
        sx: {
          height: '50px',
          cursor: 'pointer',
          backgroundColor: backgroundColor + ' !important',
          border: 'none',
          '& td[data-pinned="true"]::before': {
            backgroundColor: backgroundColor,
          },
        },
      };
    },
    state: {
      isFullScreen,
      rowSelection,
      columnVisibility: columnVisibilityModel,
      sorting,
    },
    muiTableHeadProps: {
      sx: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        padding: 0,
        color: 'white',
      },
    },
    muiTableHeadCellProps: {
      sx: theme => ({
        backgroundColor:
          (division?.code && ['19.00', '20.00'].includes(division.code) ? REPORT_COLORS.GREEN : REPORT_COLORS.YELLOW) + ' !important',
        color: '#000000',
        fontWeight: '400',
        justifyContent: 'center',
        padding: '8px 9px',
        height: '60px',
        minHeight: '60px',
        border: 'none',
        position: 'sticky',
        top: 0,
        lineHeight: '44px',
        verticalAlign: 'middle',
        '& .Mui-TableHeadCell-Content-Wrapper': {
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          display: 'flex',
        },
        '&[data-pinned="true"]:not([role="columnheader"])::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: division?.code && ['19.00', '20.00'].includes(division.code) ? REPORT_COLORS.GREEN : REPORT_COLORS.YELLOW,
          zIndex: 0,
        },
        '&[data-pinned="true"]:not([role="columnheader"])': {
          zIndex: 1,
        },
        '&:not(:last-of-type)::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: '20%',
          height: '60%',
          width: '1px',
          backgroundColor: 'rgba(0, 0, 0, 0.15)',
          boxShadow: '0 0 4px rgba(0, 0, 0, 0.2)',
        },
      }),
    },
    muiTableBodyCellProps: {
      sx: {
        border: 'none',
      },
    },
    muiTableFooterCellProps: {
      sx: theme => ({
        backgroundColor: division?.code && ['19.00', '20.00'].includes(division.code) ? REPORT_COLORS.GREEN : REPORT_COLORS.YELLOW,
        color: '#000000',
        fontWeight: '400',
        fontSize: '0.9rem',
        padding: '16px 9px',
        border: 'none',

        // Sticky footer cell background via ::before
        '&[data-pinned="true"]::before': {
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: division?.code && ['19.00', '20.00'].includes(division.code) ? REPORT_COLORS.GREEN : REPORT_COLORS.YELLOW,
          zIndex: 0,
        },

        // Maintain stickiness & layer above ::before
        '&[data-pinned="true"]': {
          zIndex: 1,
        },

        '&:not(:last-of-type)::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: '20%',
          height: '60%',
          width: '1px',
          backgroundColor: 'rgba(0, 0, 0, 0.15)',
          boxShadow: '0 0 4px rgba(0, 0, 0, 0.2)',
        },
      }),
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
      },
    },
    muiTableProps: {
      sx: {
        marginTop: '0px',
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: 'white !important',
        justifyContent: 'flex-end !important',
        display: 'flex',
        '& .css-zrlv9q': {
          justifyContent: 'flex-end !important',
        },
        '& .MuiToolbar-root.MuiToolbar-gutters.css-1613k89-MuiToolbar-root': {
          display: 'none',
        },
        // Hide the column actions button specifically
        '& .MuiButton-root[aria-label*="column"]': {
          display: 'none !important',
        },
        '& .MuiButton-root[aria-label*="Column"]': {
          display: 'none !important',
        },
        // Hide by specific MRT filter button classes
        '& .MRT_ToggleFiltersButton-root': {
          display: 'none !important',
        },
        '& .MRT_ToggleFiltersButton': {
          display: 'none !important',
        },
        '& button[class*="filter"]': {
          display: 'none !important',
        },
        '& button[class*="Filter"]': {
          display: 'none !important',
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        display: 'none',
      },
    },
    // Show MRT's top toolbar only in fullscreen mode
    enableTopToolbar: isFullScreen,
    // Control fullscreen state so we can react in our layout
    onIsFullScreenChange: setIsFullScreen,
  });

  const CustomToolbar: React.FC<CustomToolbarProps> = ({
    headingText,
    handleOpenProjectDialog,
    division,
    handleOpenLineItemDialog,
    handleAutoAssignDivisions,
    predictingDivisions,
    isSearchOpen,
    setIsSearchOpen,
    table,
  }) => {
    return (
      <Box mx={2} mt={2} display='flex' alignItems='center' justifyContent={'space-between'}>
        <Box display='flex' alignItems='center'>
          <List sx={{ fontSize: '24px !important', fontWeight: '700 !important', color: '#000000' }} />
          <Typography variant='h5' sx={{ ml: 1, mb: 0, color: '#000000' }}>
            {headingText}
          </Typography>
        </Box>
        <Stack direction={'row'} spacing={0} alignItems='center'>
          <IconOverTextButton
            icon={<Search />}
            text='Search'
            tooltip='Search line items'
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            sunken={isSearchOpen}
            showHoverEffect={true}
          />
          <Box
            sx={{
              ml: isSearchOpen ? 1 : 0,
              mr: 0,
              width: isSearchOpen ? '200px' : 0,
              transition: 'width 0.3s ease-in-out, opacity 0.3s ease-in-out, margin 0.3s ease-in-out',
              overflow: 'hidden',
              opacity: isSearchOpen ? 1 : 0,
            }}
          >
            <MRT_GlobalFilterTextField table={table} placeholder='Search' size='medium' InputProps={{ startAdornment: null }} />
          </Box>

          {division ? (
            <IconOverTextButton
              icon={<Add />}
              text='Add Line Item'
              tooltip='Add a new line item to this division'
              onClick={handleOpenLineItemDialog}
              showHoverEffect={true}
            />
          ) : (
            <IconOverTextButton
              icon={<AutoAwesome />}
              text='Auto Assign'
              tooltip='Click to predict divisions for line items using Artificial Intelligence'
              onClick={handleAutoAssignDivisions}
              loading={predictingDivisions}
              showHoverEffect={true}
            />
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <React.Fragment key={division?.id ?? 'No-Division'}>
      <Grid size={{ xs: 12 }}>
        <Card elevation={1} sx={{ padding: '0px 7px 7px 7px' }}>
          {/* Custom toolbar - show only when not in fullscreen */}
          {!isFullScreen && (
            <Box>
              <CustomToolbar
                headingText={
                  division ? `${division.code} ${division.name.toLocaleUpperCase()}` : 'NO DIVISION ASSIGNED (AND UNDEFINED CHANGE ORDERS)'
                }
                handleOpenProjectDialog={handleOpenProjectDialog}
                division={division}
                handleOpenLineItemDialog={handleOpenLineItemDialog}
                handleAutoAssignDivisions={handleAutoAssignDivisions}
                predictingDivisions={predictingDivisions}
                isSearchOpen={isSearchOpen}
                setIsSearchOpen={setIsSearchOpen}
                table={table}
              />
            </Box>
          )}
          <Box sx={{ overflowX: 'auto', px: 1, marginTop: '16px' }}>
            <Box sx={{ minWidth: '100%' }}>
              <MaterialReactTable table={table} />
            </Box>
          </Box>
        </Card>
      </Grid>
      <LineItemsBulkdEditDialog
        open={bulkEditDialogOpen}
        onClose={() => {
          setBulkEditDialogOpen(false);
        }}
        divisions={divisions}
        onSubmit={handleBulkEditSubmit}
      />
      <CreateLineItemDialog
        open={isLineItemDialogOpen}
        onClose={handleCloseLineItemDialog}
        currentDivision={division}
        divisions={divisions}
        saveLineItem={saveLineItem}
        reportGeneralInfo={reportGeneralInfo}
        projectDraws={projectDraws}
        useOriginalBudget={true}
      />
    </React.Fragment>
  );
};

export default ProjectAnalysisDivisionTable;
